import { api } from "./client";
import type { MetricAlert } from "@ligament/shared";

export interface AvailableMetric {
  key: string;
  label: string;
  category: string;
  description: string;
  defaultOperator: string;
  defaultValue: number;
  defaultSeverity: string;
  query: {
    model: string;
    explore: string;
    fields: string[];
    filters?: Record<string, string>;
  };
  measureField: string;
}

export interface ThresholdBaselineResult {
  measureField: string;
  model: string;
  explore: string;
  timeDimension: string;
  window: string;
  stats: {
    sampleSize: number;
    mean: number;
    stddev: number;
    min: number;
    max: number;
    p10: number;
    p50: number;
    p90: number;
    last: number;
  };
  suggestion: {
    direction: "high" | "low";
    warningValue: number;
    criticalValue: number;
    rationale: string;
  };
  series: { period: string; value: number }[];
}

export interface LookerExploreRef {
  name: string;
  label: string;
  description?: string;
}

export interface LookerModelSchema {
  name: string;
  label: string;
  explores: LookerExploreRef[];
}

export interface LookerFieldDef {
  name: string;
  label: string;
  type: string;
  description?: string;
}

export interface LookerExploreFields {
  model: string;
  explore: string;
  dimensions: LookerFieldDef[];
  measures: LookerFieldDef[];
}

export const metricAlertsApi = {
  lookerSchema(companyId: string) {
    return api.get<{ models: LookerModelSchema[] }>(
      `/companies/${companyId}/metric-alerts/looker-schema`,
    );
  },

  lookerExploreFields(companyId: string, model: string, explore: string) {
    const params = new URLSearchParams({ model, explore });
    return api.get<LookerExploreFields>(
      `/companies/${companyId}/metric-alerts/looker-schema/fields?${params.toString()}`,
    );
  },

  thresholdBaseline(
    companyId: string,
    body: {
      measureField: string;
      condition?: string;
      model?: string;
      explore?: string;
      filters?: Record<string, string>;
      timeDimension?: string;
      window?: string;
    },
  ) {
    return api.post<ThresholdBaselineResult>(
      `/companies/${companyId}/metric-alerts/threshold-baseline`,
      body,
    );
  },

  async list(companyId: string, opts?: { acknowledged?: boolean; limit?: number }) {
    const params = new URLSearchParams();
    if (opts?.acknowledged !== undefined) params.set("acknowledged", String(opts.acknowledged));
    if (opts?.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return api.get<MetricAlert[]>(`/companies/${companyId}/metric-alerts${qs ? `?${qs}` : ""}`);
  },

  async count(companyId: string) {
    return api.get<{ count: number }>(`/companies/${companyId}/metric-alerts/count`);
  },

  async availableMetrics(companyId: string) {
    return api.get<AvailableMetric[]>(`/companies/${companyId}/metric-alerts/available-metrics`);
  },

  async acknowledge(companyId: string, alertId: string) {
    return api.patch<MetricAlert>(`/companies/${companyId}/metric-alerts/${alertId}/acknowledge`, {});
  },

  async acknowledgeAll(companyId: string) {
    return api.post<{ ok: boolean }>(`/companies/${companyId}/metric-alerts/acknowledge-all`, {});
  },
};
